import type { TooltipProps as BaseTooltipProps } from '@mui/joy/Tooltip';
import { default as BaseTooltip } from '@mui/joy/Tooltip';
import { clsx } from 'clsx';
import { forwardRef, memo, useMemo } from 'react';
import Stack from '../../_components/Stack';
import { slowCn } from '../../lib/utils';
import { useContainerElementContext } from '../../providers/ContainerElementContext';
import { Typography } from '../Typography';
import { UA_TOOLTIP_CLASS, UA_TOOLTIP_DATA_ATTRIBUTE } from './const';
import { tooltipArrowClassname, tooltipVariants } from './styles';
import { TooltipModes, type TooltipProps } from './types';
import { getTooltipFallbackPlacementByPlacement } from './utils';

const Tooltip = forwardRef<HTMLDivElement, TooltipProps>((props, ref) => {
  const {
    className,
    arrow = true,
    enterDelay = 0,
    title,
    description,
    titleNode,
    startDecoratorComponent: StartDecoratorComponent,
    startDecoratorNode,
    endDecoratorNode,
    offset = 10,
    modifiers,
    disabled = false,
    mode = TooltipModes.LIGHT,
    slotProps,
    ...rest
  } = props;

  const globalContainerEl = useContainerElementContext();
  const { className: titleClassName, style: titleStyle, maxNumberOfLines } = slotProps?.title ?? {};

  const tooltipContent = useMemo(() => {
    if (titleNode) return titleNode;

    return title || description ? (
      <>
        {title ? (
          <Stack alignItems="center" className="gap-sm text-placeholder text-xs" direction="row">
            {StartDecoratorComponent ? (
              <StartDecoratorComponent height={12} width={12} />
            ) : (
              startDecoratorNode
            )}
            <Typography
              className={clsx(
                /**
                 * The tooltip is rendered in light mode with a dark background, which will not
                 * work well with other light mode text tokens such as text-primary or text-secondary
                 * therefore we try to force upon light mode tokens and dark mode tokens that will work
                 * in the tooltip itself, and its direct child.

                 * `text-white` and `text-black` will work with direct text as children, but
                 * for `<p>...</p>` we need to try to force the correct classes to the direct child also
                 */

                /**
                 * Styles inline <code> elements inside tooltip content
                 * to match design specs (background, text color, font-size, line-height).
                 * Figma: https://www.figma.com/design/MEUM4gvEDCStVYPzPOtcit/Base-ES?node-id=6421-139702&m=dev
                 */

                'text-white dark:text-black [&>*]:text-white [&>*]:dark:text-black [&_code]:bg-[var(--palette-gray-dark-500)] [&_code]:text-white [&_code]:text-[10px] break-words',
                titleClassName,
              )}
              maxNumberOfLines={maxNumberOfLines}
              style={titleStyle}
              variant="text-xs"
              weight={description ? 'semi-bold' : 'regular'}
            >
              {title}
            </Typography>
            {endDecoratorNode}
          </Stack>
        ) : null}
        {description ? (
          <Typography
            className="dark:text-placeholder text-utility-gray-300"
            variant="text-xs"
            weight="medium"
          >
            {description}
          </Typography>
        ) : null}
      </>
    ) : (
      ''
    );
  }, [
    StartDecoratorComponent,
    description,
    endDecoratorNode,
    startDecoratorNode,
    title,
    titleClassName,
    titleNode,
    titleStyle,
    maxNumberOfLines,
  ]);

  // @ts-expect-error -- typescript wants us to provide root.open prop,
  // but setting it to true/false/undefined prevents tooltip from opening.
  const tooltipSlotProps = useMemo<BaseTooltipProps['slotProps']>(() => {
    return {
      root: {
        container: globalContainerEl,
        className: tooltipVariants({
          description: Boolean(description),
          className: clsx(
            'isolate',
            // popper's `hide` modifier flags this when the anchor is clipped by a scroll
            // container; without it the tooltip stays pinned in the viewport over unrelated content
            'data-[popper-reference-hidden]:invisible data-[popper-reference-hidden]:pointer-events-none',
            UA_TOOLTIP_CLASS,
            className,
            slotProps?.rootClassName,
          ),
        }),
        'data-color-scheme': mode,
        [UA_TOOLTIP_DATA_ATTRIBUTE]: '',
        ...slotProps?.root,
      },
      arrow: {
        className: slowCn(tooltipArrowClassname, slotProps?.arrowClassName),
      },
    };
  }, [
    globalContainerEl,
    description,
    className,
    slotProps?.rootClassName,
    slotProps?.root,
    slotProps?.arrowClassName,
    mode,
  ]);

  const finalModifiers = useMemo<BaseTooltipProps['modifiers']>(() => {
    const baseModifiers: BaseTooltipProps['modifiers'] = [
      // Offset must come first - controls distance from reference element
      { name: 'offset', options: { offset: [0, offset] } },
      // Flip tries different placements if overflow is detected
      {
        name: 'flip',
        enabled: true,
        options: {
          flipAlignment: true,
          fallbackPlacements: getTooltipFallbackPlacementByPlacement(rest.placement),
          rootBoundary: 'viewport',
        },
      },
      // Shift adjusts position to keep tooltip within viewport bounds
      {
        name: 'shift',
        enabled: true,
        options: {
          rootBoundary: 'viewport',
          padding: 16,
          crossAxis: true,
          mainAxis: true,
        },
      },
      // PreventOverflow keeps tooltip within viewport bounds
      {
        name: 'preventOverflow',
        enabled: true,
        options: {
          rootBoundary: 'viewport',
          padding: 16,
          altAxis: true,
          tether: false,
        },
      },
    ];

    return [...baseModifiers, ...(modifiers || [])];
  }, [modifiers, offset, rest.placement]);

  return (
    <BaseTooltip
      arrow={arrow}
      enterDelay={enterDelay}
      modifiers={finalModifiers}
      ref={ref}
      slotProps={tooltipSlotProps}
      title={disabled ? null : tooltipContent}
      variant="solid"
      {...rest}
    />
  );
});

Tooltip.displayName = 'Tooltip';

export default memo(Tooltip);
